/**
 * @fileoverview API service for mobile chat endpoints.
 * All requests include credentials so the SAP SSO cookie is forwarded
 * automatically by the browser.
 */

const mobileIndex = window.location.pathname.indexOf('/m/');
const proxyPrefix = mobileIndex > 0 ? window.location.pathname.substring(0, mobileIndex) : '';
const API_BASE = `${proxyPrefix}/api`;

const MobileChatService = {
  /**
   * Fetch mobile chat agent configuration from backend.
   * @returns {Promise<Object>} Agent config (projectId, agentId, agentName, etc.)
   */
  async getConfig() {
    const res = await fetch(`${API_BASE}/m/config`, { credentials: 'include' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.detail || 'Failed to load config');
    }
    return res.json();
  },

  /**
   * Send a chat message.
   * @param {Object} payload - { messages, conversationId, previousResponseId, ephemeral }
   * @returns {Promise<Object>} Chat response
   */
  async sendMessage(payload) {
    const res = await fetch(`${API_BASE}/m/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.detail || 'Chat request failed');
    }
    return res.json();
  },

  /**
   * List conversations for the mobile user.
   * @returns {Promise<Array>} List of conversation objects
   */
  async getConversations() {
    const res = await fetch(`${API_BASE}/m/conversations`, { credentials: 'include' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.detail || 'Failed to load conversations');
    }
    return res.json();
  },

  /**
   * Fetch messages for a conversation.
   * @param {string} conversationId
   * @returns {Promise<Array>} List of message objects
   */
  async getMessages(conversationId) {
    const res = await fetch(`${API_BASE}/m/conversations/${encodeURIComponent(conversationId)}/messages`, {
      credentials: 'include',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.detail || 'Failed to load messages');
    }
    return res.json();
  },
};

export default MobileChatService;
